import React, { useState } from 'react';
import { 
  Globe, 
  ShieldCheck, 
  ShieldAlert, 
  AlertTriangle, 
  CheckCircle2, 
  Lock, 
  Calendar, 
  Server, 
  ExternalLink, 
  AlertCircle,
  Info,
  Layers,
  ArrowRight
} from 'lucide-react';
import { analyzeWebsite } from '../api/websites';
import { WebsiteAnalysisResult } from '../types';
import { WEBSITE_PRESETS } from '../data/websiteScenarios';

export const WebsiteCheckerPage: React.FC = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<WebsiteAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCheck = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!url.trim()) return;

    setLoading(true);
    setError(null);
    try {
      const res = await analyzeWebsite(url.trim());
      setResult(res);
    } catch (err: any) {
      setError(err.message || 'Error assessing website.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreset = (presetUrl: string) => {
    setUrl(presetUrl);
    setResult(null);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-950 text-blue-300 text-xs font-semibold border border-blue-800">
          <Globe className="w-3.5 h-3.5" />
          <span>Website Safety & Domain Risk Analyzer</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white">
          Check a Website Before You Click or Pay
        </h1>
        <p className="text-slate-400 text-sm max-w-xl mx-auto">
          Analyze domain age, SSL security, brand impersonation risks, typosquatting, and deceptive URL redirections targeting Australian users.
        </p>
      </div>

      {/* Input Box */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6">
        <form onSubmit={handleCheck} className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="e.g. https://mygov-secure-portal-au.com or auspost.com.au"
                required
                className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-slate-950 border border-slate-700 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-blue-500 shadow-inner"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !url.trim()}
              className="px-7 py-3.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-950/40 transition-all flex items-center justify-center gap-2 flex-shrink-0"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Inspecting Domain...</span>
                </>
              ) : (
                <>
                  <Globe className="w-4 h-4" />
                  <span>Check Website</span>
                </>
              )}
            </button>
          </div>

          {/* Quick preset URLs */}
          <div className="space-y-2">
            <span className="text-xs text-slate-400 font-medium">Try test samples:</span>
            <div className="flex flex-wrap gap-2 text-xs">
              {WEBSITE_PRESETS.map((preset, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handlePreset(preset.url)}
                  className="px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 font-mono text-[11px] border border-slate-700/60"
                >
                  {preset.domain} ({preset.riskLevel})
                </button>
              ))}
            </div>
          </div>
        </form>

        {/* Essential Legal & Risk Assessment Disclaimer */}
        <div className="p-4 rounded-xl bg-slate-950/80 border border-slate-800 flex items-start gap-3 text-xs text-slate-400">
          <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            <strong className="text-slate-300">Important Notice: </strong>
            This automated tool provides a threat risk assessment based on domain age, SSL telemetry, registrar reputation, and brand similarity heuristics. It is an automated risk assessment, not a legal guarantee that a website is safe or malicious.
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-rose-950/80 border border-rose-800 text-rose-200 text-sm flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* RESULTS DISPLAY */}
      {result && (
        <div className="space-y-8 animate-fade-in">
          {/* Main Risk Status */}
          <div className={`p-6 sm:p-8 rounded-3xl border shadow-2xl space-y-4 ${
            result.riskLevel === 'HIGH'
              ? 'bg-rose-950/40 border-rose-700 text-rose-200'
              : result.riskLevel === 'MEDIUM'
                ? 'bg-amber-950/40 border-amber-700 text-amber-200'
                : 'bg-emerald-950/40 border-emerald-700 text-emerald-200'
          }`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800/80 pb-4">
              <div>
                <span className="text-xs uppercase font-bold tracking-wider opacity-80">Website Risk Assessment</span>
                <h3 className="text-2xl font-extrabold text-white flex items-center gap-2">
                  <span>Website Risk:</span>
                  <span className={`px-3 py-0.5 rounded-full text-base font-bold ${
                    result.riskLevel === 'HIGH' ? 'bg-rose-500 text-white' : result.riskLevel === 'MEDIUM' ? 'bg-amber-500 text-slate-950' : 'bg-emerald-500 text-white'
                  }`}>
                    {result.riskLevel === 'HIGH' && '🔴 HIGH RISK'}
                    {result.riskLevel === 'MEDIUM' && '🟡 MEDIUM RISK'}
                    {result.riskLevel === 'LOW' && '🟢 LOW RISK'}
                  </span>
                </h3>
              </div>
              <div className="text-right">
                <span className="text-xs font-mono text-slate-300">Domain: {result.domain}</span>
              </div>
            </div>

            {/* Impersonation alert */}
            {result.potentialImpersonation && (
              <div className="p-3.5 rounded-xl bg-rose-900/60 border border-rose-700 text-rose-100 flex items-center gap-3 text-xs font-medium">
                <AlertTriangle className="w-5 h-5 text-rose-300 flex-shrink-0" />
                <span>
                  <strong>Potential Impersonation Alert: </strong>
                  This domain exhibits characteristics designed to mimic <u>{result.potentialImpersonation}</u>.
                </span>
              </div>
            )}

            {/* Domain Telemetry Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs pt-2">
              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 space-y-1">
                <div className="text-slate-400 flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" /> Domain Age
                </div>
                <div className="text-sm font-bold text-white">
                  {result.domainAgeDays} days old
                </div>
              </div>

              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 space-y-1">
                <div className="text-slate-400 flex items-center gap-1">
                  <Lock className="w-3.5 h-3.5" /> HTTPS / SSL
                </div>
                <div className="text-sm font-bold text-emerald-400">
                  {result.hasHttps ? 'Valid SSL' : 'No SSL'}
                </div>
              </div>

              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 space-y-1">
                <div className="text-slate-400 flex items-center gap-1">
                  <Server className="w-3.5 h-3.5" /> Registrar
                </div>
                <div className="text-xs font-bold text-white truncate" title={result.registrar}>
                  {result.registrar}
                </div>
              </div>

              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 space-y-1">
                <div className="text-slate-400 flex items-center gap-1">
                  <Layers className="w-3.5 h-3.5" /> Typosquatting
                </div>
                <div className={`text-sm font-bold ${result.typosquattingDetected ? 'text-rose-400' : 'text-emerald-400'}`}>
                  {result.typosquattingDetected ? 'Detected' : 'None Detected'}
                </div>
              </div>
            </div>
          </div>

          {/* Warning Signs & Recommendations */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Warning Signs */}
            <div className="p-6 rounded-3xl bg-slate-900 border border-slate-800 space-y-3">
              <h4 className="text-sm font-bold text-slate-200 uppercase tracking-wider flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span>Observed Threat Indicators ({result.warningSigns.length})</span>
              </h4>
              {result.warningSigns.length === 0 ? (
                <p className="text-xs text-slate-400">No red flags or malicious signatures found on this domain.</p>
              ) : (
                <ul className="space-y-2 text-xs text-slate-300">
                  {result.warningSigns.map((sign, idx) => (
                    <li key={idx} className="flex items-start gap-2 bg-slate-950 p-2.5 rounded-lg border border-slate-800">
                      <span className="text-rose-400 font-bold">•</span>
                      <span>{sign}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Recommendations */}
            <div className="p-6 rounded-3xl bg-slate-900 border border-slate-800 space-y-3">
              <h4 className="text-sm font-bold text-slate-200 uppercase tracking-wider flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span>Safe Browsing Guidance</span>
              </h4>
              <ul className="space-y-2 text-xs text-slate-300">
                {result.safeRecommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2 bg-slate-950 p-2.5 rounded-lg border border-slate-800">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
